import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Col, DatePicker, Dropdown, Empty, Input, Pagination, Row, Select, Skeleton, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import { DownloadOutlined, EyeOutlined, FileSearchOutlined, FilterOutlined, MoreOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { fetchDocumentCenter, downloadDocumentCenterPdf, DocumentCenterItem, DocumentCenterType } from '../modules/document-center/api/documentCenterApi';
import { getDocumentCenterStatusColor, getDocumentCenterStatusLabel } from '../modules/document-center/documentCenterStatus';
import { groupDocumentsByOrder, type DocumentCenterGroup } from '../modules/document-center/documentCenterGrouping';
import {
  canSeeDocumentCenterAmounts,
  getVisibleDocumentCenterTypeFilters,
} from '../modules/document-center/documentCenterVisibility';
import { authService } from '../services/auth';
import { formatDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/currency';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';
import '../components/layout/AppPage.css';
import './DocumentCenter.css';

const { RangePicker } = DatePicker;
const { Text } = Typography;

// Orders (groups) shown per page; documents inside a group are never paginated.
const GROUPS_PER_PAGE = 10;

const GROUP_KIND_TAG: Record<DocumentCenterGroup['kind'], { color: string; labelKey: string }> = {
  CUSTOMER_ORDER: { color: 'blue', labelKey: 'documentCenter:groups.customerOrder' },
  SUPPLIER_ORDER: { color: 'gold', labelKey: 'documentCenter:groups.supplierOrder' },
  NONE: { color: 'default', labelKey: 'documentCenter:groups.noOrder' },
};

const buildDocumentDownloadFileName = (record: DocumentCenterItem): string => {
  const fileName = record.fileName || `${record.number || record.id}.pdf`;
  return fileName.replace(/[\\/:*?"<>|]+/g, '-');
};

const DocumentCenter: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['documentCenter', 'common', 'pz', 'wz', 'invoices', 'orders']);
  const feedback = useFeedback();
  const [documents, setDocuments] = useState<DocumentCenterItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [type, setType] = useState<'ALL' | DocumentCenterType>('ALL');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [groupPage, setGroupPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(true);
  // Distinct (status, representative type) pairs seen in the unfiltered result
  // set, used to populate the Status dropdown. Captured only when no status
  // filter is active so the option list doesn't collapse after a selection.
  const [statusPool, setStatusPool] = useState<Array<{ value: string; type: DocumentCenterType; workflow: boolean }>>([]);

  const canViewSalesDocuments = authService.canViewSalesDocuments();
  const canViewPurchaseDocuments = authService.canViewPurchaseDocuments();
  const canViewWarehouseDocuments = authService.canViewWarehouseDocuments();
  const documentCenterCapabilities = useMemo(() => ({
    canViewSalesDocuments,
    canViewPurchaseDocuments,
    canViewWarehouseDocuments,
  }), [canViewPurchaseDocuments, canViewSalesDocuments, canViewWarehouseDocuments]);
  const canSeeAmounts = canSeeDocumentCenterAmounts(documentCenterCapabilities);

  const visibleTypeFilters = useMemo(
    () => getVisibleDocumentCenterTypeFilters(documentCenterCapabilities),
    [documentCenterCapabilities]
  );

  const advancedFilterCount =
    (type !== 'ALL' ? 1 : 0) +
    (status ? 1 : 0) +
    (dateRange ? 1 : 0);
  const hasAnyFilter = search !== '' || advancedFilterCount > 0;

  const groups = useMemo(() => groupDocumentsByOrder(documents), [documents]);
  const visibleGroups = useMemo(
    () => groups.slice((groupPage - 1) * GROUPS_PER_PAGE, groupPage * GROUPS_PER_PAGE),
    [groups, groupPage]
  );

  const statusSelectOptions = useMemo(
    () => {
      const options = statusPool
        .map(({ value, type: docType, workflow }) => ({ value, label: getDocumentCenterStatusLabel(t, docType, value, workflow) }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pl'));
      // Safety net: a selected status must always carry a label even if it is no
      // longer in the current pool (e.g. after a type switch), so the Select
      // never shows a bare raw value.
      if (status && !options.some((option) => option.value === status)) {
        options.push({ value: status, label: status });
      }
      return options;
    },
    [statusPool, status, t]
  );

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const [from, to] = dateRange || [];
      const data = await fetchDocumentCenter({
        type: type === 'ALL' ? undefined : type,
        search: search.trim() || undefined,
        status,
        fromDate: from ? from.startOf('day').toISOString() : undefined,
        toDate: to ? to.endOf('day').toISOString() : undefined,
      });
      setDocuments(data);
      setGroupPage(1);
      // Refresh the Status dropdown pool only from an unfiltered result set.
      if (!status) {
        const seen = new Map<string, { type: DocumentCenterType; workflow: boolean }>();
        data.forEach((doc) => {
          if (doc.status && !seen.has(doc.status)) seen.set(doc.status, { type: doc.type, workflow: Boolean(doc.workflow) });
        });
        setStatusPool(Array.from(seen, ([value, meta]) => ({ value, type: meta.type, workflow: meta.workflow })));
      }
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'documentCenter:errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const resetFilters = () => {
    setSearch('');
    setStatus(undefined);
    setDateRange(null);
    setType('ALL');
  };

  const downloadPdf = async (record: DocumentCenterItem) => {
    if (!record.pdfPath) return;
    setDownloading(record.id);
    try {
      const blob = await downloadDocumentCenterPdf(record.pdfPath);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = buildDocumentDownloadFileName(record);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      feedback.error(
        getApiErrorMessage(error, t, 'documentCenter:errors.downloadFailed', undefined, {
          number: record.number || record.id,
        }),
      );
    } finally {
      setDownloading(null);
    }
  };

  const getRowMenuItems = (record: DocumentCenterItem): MenuProps['items'] => [
    { key: 'open', icon: <EyeOutlined />, label: t('documentCenter:actions.open') },
    {
      key: 'download',
      icon: <DownloadOutlined />,
      label: record.pdfPath ? t('documentCenter:actions.download') : t('documentCenter:actions.noPdf'),
      disabled: !record.pdfPath,
    },
  ];

  // Per-group document table. The order itself is the group header, so rows
  // carry only the document facts; the company column is shown solely in the
  // "no order" group, where the header has no party to name.
  const buildColumns = (includeCompany: boolean): ColumnsType<DocumentCenterItem> => [
    {
      title: t('common:actions'),
      key: 'actions',
      width: 56,
      align: 'center',
      fixed: 'left',
      render: (_, record) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: getRowMenuItems(record),
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              if (key === 'open') {
                navigate(record.sourcePath);
              }
              if (key === 'download') {
                void downloadPdf(record);
              }
            },
          }}
        >
          <Button
            icon={<MoreOutlined />}
            size="small"
            loading={downloading === record.id}
            onClick={(event) => event.stopPropagation()}
            title={t('common:actions')}
            aria-label={t('common:actions')}
          />
        </Dropdown>
      ),
    },
    {
      title: t('documentCenter:columns.number'),
      dataIndex: 'number',
      key: 'number',
      width: 340,
      render: (number: string, record) => (
        <div className="document-center-identity">
          <button className="document-center-name-button" onClick={() => navigate(record.sourcePath)}>
            <span>{number || record.fileName || '-'}</span>
          </button>
          <Tag>{t(`documentCenter:types.${record.type}`)}</Tag>
        </div>
      ),
    },
    {
      title: t('documentCenter:columns.date'),
      dataIndex: 'date',
      key: 'date',
      width: 130,
      render: (date?: string | null) => date ? formatDate(date) : '-',
    },
    ...(includeCompany ? [{
      title: t('documentCenter:columns.company'),
      key: 'company',
      width: 220,
      render: (_: unknown, record: DocumentCenterItem) => (
        <div className="document-center-identity">
          <span className="document-center-company">{record.customerName || record.supplierName || '-'}</span>
          {record.customerName && record.supplierName && (
            <small className="document-center-identity__meta">{record.supplierName}</small>
          )}
        </div>
      ),
    }] : []),
    {
      title: t('documentCenter:columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (value: string | null | undefined, record) => value
        ? <Tag color={getDocumentCenterStatusColor(value)}>{getDocumentCenterStatusLabel(t, record.type, value, Boolean(record.workflow))}</Tag>
        : '-',
    },
    ...(canSeeAmounts ? [{
      title: t('documentCenter:columns.amount'),
      key: 'amount',
      width: 150,
      align: 'right' as const,
      render: (_: unknown, record: DocumentCenterItem) => (
        record.amount != null ? formatCurrency(Number(record.amount), record.currency || 'PLN') : '-'
      ),
    }] : []),
  ];

  const renderGroup = (group: DocumentCenterGroup) => {
    const kindTag = GROUP_KIND_TAG[group.kind];
    return (
      <div className="document-center-group" key={group.key}>
        <div className="document-center-group__header">
          <div className="document-center-group__title">
            <Tag color={kindTag.color}>{t(kindTag.labelKey)}</Tag>
            {group.orderPath ? (
              <button className="document-center-order-button" onClick={() => navigate(group.orderPath as string)}>
                <span>{group.orderNumber}</span>
              </button>
            ) : null}
            {group.partyName ? <Text className="document-center-group__party">{group.partyName}</Text> : null}
            {group.clientOrderNumber ? (
              <Text type="secondary">{t('documentCenter:groups.clientOrderNumber', { number: group.clientOrderNumber })}</Text>
            ) : null}
          </div>
          <Text type="secondary">{t('documentCenter:groups.documentsCount', { count: group.documents.length })}</Text>
        </div>
        <Table
          className="document-center-table"
          rowKey={(record) => `${record.type}:${record.id}`}
          columns={buildColumns(group.kind === 'NONE')}
          dataSource={group.documents}
          size="middle"
          tableLayout="fixed"
          pagination={false}
          scroll={{ x: 880 }}
        />
      </div>
    );
  };

  return (
    <div className="app-page document-center-page">
      <section className="app-page__card">
        <div className="app-page__toolbar">
          <Input
            className="app-page__toolbar-search"
            placeholder={t('documentCenter:filters.search')}
            prefix={<SearchOutlined />}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onPressEnter={loadDocuments}
            allowClear
          />
          <div className="app-page__toolbar-actions">
            <Badge count={advancedFilterCount} size="small" offset={[-2, 2]}>
              <Button
                icon={<FilterOutlined />}
                type={filtersOpen || advancedFilterCount > 0 ? 'primary' : 'default'}
                ghost={filtersOpen || advancedFilterCount > 0}
                onClick={() => setFiltersOpen((open) => !open)}
              >
                {t('documentCenter:filters.toggle')}
              </Button>
            </Badge>
            {hasAnyFilter && (
              <Button type="link" onClick={resetFilters}>
                {t('common:clearFilters')}
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={loadDocuments} loading={loading}>
              {t('common:refresh')}
            </Button>
            <Button type="primary" icon={<FileSearchOutlined />} onClick={loadDocuments}>
              {t('documentCenter:actions.search')}
            </Button>
          </div>
        </div>

        {filtersOpen && (
          <Row gutter={[12, 12]} className="app-page__filters">
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                aria-label={t('documentCenter:filters.type')}
                placeholder={t('documentCenter:filters.type')}
                value={type}
                style={{ width: '100%' }}
                onChange={setType}
                options={visibleTypeFilters.map((item) => ({
                  value: item.value,
                  label: t(item.labelKey),
                }))}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                aria-label={t('documentCenter:filters.status')}
                allowClear
                value={status}
                style={{ width: '100%' }}
                placeholder={t('documentCenter:filters.statusAll')}
                options={statusSelectOptions}
                onChange={(value) => setStatus(value || undefined)}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <RangePicker
                aria-label={t('documentCenter:filters.dateRange')}
                style={{ width: '100%' }}
                value={dateRange}
                onChange={(value) => setDateRange(value)}
              />
            </Col>
          </Row>
        )}

        {loading && documents.length === 0 ? (
          <div className="app-page__card-body">
            <Skeleton active paragraph={{ rows: 6 }} />
          </div>
        ) : groups.length === 0 ? (
          <div className="app-page__card-body">
            <Empty description={t('documentCenter:empty')} />
          </div>
        ) : (
          <>
            <div className="document-center-groups">
              {visibleGroups.map(renderGroup)}
            </div>
            {groups.length > GROUPS_PER_PAGE && (
              <div className="document-center-pagination">
                <Pagination
                  current={groupPage}
                  pageSize={GROUPS_PER_PAGE}
                  total={groups.length}
                  showSizeChanger={false}
                  onChange={setGroupPage}
                />
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default DocumentCenter;
